import React, { useState, useRef, useEffect } from "react";
import { cn } from "../../styles";
import { StyledContainerProps } from "../../props";
import { ScrollArea } from "../../layouts/ScrollArea";
import {
  AppDrawerGroup,
  AppDrawerLink,
  AppDrawerNavigation,
  AppDrawerCopyright,
} from "./Navigation";

export type {
  AppDrawerNavigationProps,
  AppDrawerGroupProps,
  AppDrawerLinkProps,
  AppDrawerCopyrightProps,
} from "./Navigation";

export interface AppDrawerProps extends StyledContainerProps {}

/**
 * Side navigation drawer component with dark mode styling and automatic scroll indicators.
 * Provides a vertical navigation container with gradient fade effect when content is scrollable.
 * Always renders in dark mode regardless of app theme. Designed for use with AppDrawer.Navigation,
 * AppDrawer.Group, AppDrawer.Link, and AppDrawer.Copyright sub-components for complete navigation structure.
 */
export const AppDrawer = ({ children, className }: AppDrawerProps) => {
  const [canScrollDown, setCanScrollDown] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;

    const check = () => {
      const { scrollTop, scrollHeight, clientHeight } = node;
      setCanScrollDown(scrollTop + clientHeight < scrollHeight - 10);
    };

    // Initialize and attach
    check();
    node.addEventListener("scroll", check);
    const observer = new ResizeObserver(check);
    observer.observe(node);

    return () => {
      node.removeEventListener("scroll", check);
      observer.disconnect();
    };
  }, []);

  return (
    <div
      className={cn(
        "dark",
        "h-full relative",
        "shadow-md dark:border-back-700",
        "bg-back-800 dark:border-r dark:bg-back-900 text-front-50",
        className,
      )}
    >
      <ScrollArea
        tabIndex={-1}
        ref={scrollRef}
        className="h-full"
        contentClassName="h-full flex flex-col min-w-max" // min-w-max fixes ResizeObserver race condition
      >
        {children}
      </ScrollArea>

      <div
        className={cn(
          "absolute bottom-0 left-0 right-0 h-16 pointer-events-none",
          "bg-gradient-to-t from-back-800 dark:from-back-900 to-transparent",
          "transition-opacity duration-300",
          canScrollDown ? "opacity-100" : "opacity-0",
        )}
      />
    </div>
  );
};

AppDrawer.Navigation = AppDrawerNavigation;
AppDrawer.Group = AppDrawerGroup;
AppDrawer.Link = AppDrawerLink;
AppDrawer.Copyright = AppDrawerCopyright;
